import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SkillConstellation = () => {
  const [activeSkill, setActiveSkill] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const skillCategories = [
    {
      name: 'Frontend Mastery',
      color: '#00d4ff',
      icon: 'Monitor',
      skills: [
        { name: 'React', level: 95, years: 5, x: 20, y: 25, projects: 47 },
        { name: 'TypeScript', level: 90, years: 4, x: 35, y: 15, projects: 32 },
        { name: 'Next.js', level: 88, years: 3, x: 50, y: 30, projects: 28 },
        { name: 'Vue.js', level: 75, years: 2, x: 65, y: 20, projects: 15 },
        { name: 'Tailwind CSS', level: 92, years: 3, x: 80, y: 35, projects: 41 }
      ]
    },
    {
      name: 'Backend Architecture',
      color: '#00ff88',
      icon: 'Server',
      skills: [
        { name: 'Node.js', level: 93, years: 5, x: 15, y: 55, projects: 38 },
        { name: 'Python', level: 87, years: 6, x: 30, y: 65, projects: 29 },
        { name: 'PostgreSQL', level: 85, years: 4, x: 45, y: 50, projects: 33 },
        { name: 'MongoDB', level: 80, years: 3, x: 60, y: 70, projects: 22 },
        { name: 'GraphQL', level: 78, years: 2, x: 75, y: 60, projects: 18 }
      ]
    },
    {
      name: 'Cloud & DevOps',
      color: '#8844ff',
      icon: 'Cloud',
      skills: [
        { name: 'AWS', level: 89, years: 4, x: 25, y: 85, projects: 31 },
        { name: 'Docker', level: 86, years: 3, x: 40, y: 90, projects: 26 },
        { name: 'Kubernetes', level: 72, years: 2, x: 55, y: 95, projects: 14 },
        { name: 'CI/CD', level: 84, years: 3, x: 70, y: 85, projects: 24 },
        { name: 'Terraform', level: 68, years: 1, x: 85, y: 90, projects: 9 }
      ]
    }
  ];

  const allSkills = skillCategories.flatMap(category => 
    category.skills.map(skill => ({ ...skill, category: category.name, color: category.color }))
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      const rect = e.currentTarget?.getBoundingClientRect();
      if (rect) {
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };

    const container = document.getElementById('constellation-container');
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
      return () => container.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  const getSkillSize = (level) => {
    return Math.max(8, (level / 100) * 16);
  };

  const getConnectionOpacity = (skill1, skill2) => {
    const distance = Math.sqrt(
      Math.pow(skill1.x - skill2.x, 2) + Math.pow(skill1.y - skill2.y, 2)
    );
    return Math.max(0, 1 - distance / 50);
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="font-orbitron font-bold text-3xl text-foreground mb-4">
          Skill Constellation
        </h2>
        <p className="text-text-secondary font-inter max-w-2xl mx-auto">
          An interactive star map of technical proficiencies, where each skill shines with the intensity of mastery
        </p>
      </motion.div>

      {/* Category Legend */}
      <div className="flex flex-wrap justify-center gap-6 mb-8">
        {skillCategories.map((category, index) => (
          <motion.div
            key={category.name}
            className="flex items-center space-x-3 glass-morphism rounded-lg px-4 py-2 cyber-border"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${category.color}20`, borderColor: category.color }}
            >
              <Icon name={category.icon} size={16} style={{ color: category.color }} />
            </div>
            <span className="font-inter font-medium text-foreground text-sm">
              {category.name}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Constellation Map */}
      <div className="relative">
        <div
          id="constellation-container"
          className="relative w-full h-96 glass-morphism rounded-2xl cyber-border overflow-hidden cursor-crosshair"
        >
          {/* Background Grid */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full">
              <defs>
                <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#00d4ff" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <defs>
              <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#8844ff" stopOpacity="0.2" />
                <stop offset="100%" stopColor="#00ff88" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            {allSkills.map((skill1, i) =>
              allSkills.slice(i + 1).map((skill2, j) => {
                const opacity = getConnectionOpacity(skill1, skill2);
                if (opacity < 0.1) return null;
                
                return (
                  <motion.line
                    key={`${skill1.name}-${skill2.name}`}
                    x1={`${skill1.x}%`}
                    y1={`${skill1.y}%`}
                    x2={`${skill2.x}%`}
                    y2={`${skill2.y}%`}
                    stroke="url(#connectionGradient)"
                    strokeWidth="1"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{
                      pathLength: 1,
                      opacity: activeSkill ? 
                        (activeSkill.name === skill1.name || activeSkill.name === skill2.name ? opacity * 0.8 : opacity * 0.2) :
                        opacity * 0.4
                    }}
                    transition={{ duration: 1.5, delay: (i + j) * 0.05 }}
                  />
                );
              })
            )}
          </svg>

          {/* Skill Stars */}
          {allSkills.map((skill, index) => (
            <motion.div
              key={skill.name}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
              style={{
                left: `${skill.x}%`,
                top: `${skill.y}%`
              }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: 1, 
                opacity: 1,
                x: activeSkill?.name === skill.name ? (mousePosition.x - skill.x) * 0.05 : 0,
                y: activeSkill?.name === skill.name ? (mousePosition.y - skill.y) * 0.05 : 0
              }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.05,
                x: { duration: 0.3 },
                y: { duration: 0.3 }
              }}
              whileHover={{ scale: 1.2 }}
              onClick={() => setActiveSkill(activeSkill?.name === skill.name ? null : skill)}
            >
              {/* Skill Star */}
              <div
                className="rounded-full border-2 cyber-glow transition-all duration-300"
                style={{
                  width: `${getSkillSize(skill.level)}px`,
                  height: `${getSkillSize(skill.level)}px`,
                  backgroundColor: skill.color,
                  borderColor: skill.color,
                  boxShadow: `0 0 ${skill.level / 5}px ${skill.color}80`
                }}
              >
                {/* Pulsing Core */}
                <motion.div
                  className="absolute inset-1 rounded-full"
                  style={{ backgroundColor: skill.color }}
                  animate={{
                    opacity: [0.6, 1, 0.6],
                    scale: [0.8, 1, 0.8]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>

              {/* Skill Label */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-border">
                  <div className="text-center">
                    <div className="font-mono font-bold text-sm text-foreground">
                      {skill.name}
                    </div>
                    <div className="text-xs text-text-secondary mt-1">
                      {skill.level}% • {skill.years}y • {skill.projects} projects
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Active Skill Details */}
          {activeSkill && (
            <motion.div
              className="absolute top-4 right-4 glass-morphism rounded-xl p-4 cyber-border-active max-w-xs"
              initial={{ opacity: 0, scale: 0.8, x: 20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 0.8, x: 20 }}
            >
              <div className="flex items-center space-x-3 mb-3">
                <div
                  className="w-8 h-8 rounded-full"
                  style={{ backgroundColor: activeSkill.color }}
                />
                <div>
                  <h4 className="font-inter font-bold text-foreground">
                    {activeSkill.name}
                  </h4>
                  <p className="text-xs text-text-secondary">
                    {activeSkill.category}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-text-secondary">Proficiency</span>
                  <span className="font-mono font-bold text-foreground">
                    {activeSkill.level}%
                  </span>
                </div>
                <div className="w-full bg-surface rounded-full h-2">
                  <motion.div
                    className="h-2 rounded-full"
                    style={{ backgroundColor: activeSkill.color }}
                    initial={{ width: 0 }}
                    animate={{ width: `${activeSkill.level}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                  <div>
                    <span className="text-text-secondary">Experience</span>
                    <div className="font-mono font-bold text-foreground">
                      {activeSkill.years} years
                    </div>
                  </div>
                  <div>
                    <span className="text-text-secondary">Projects</span>
                    <div className="font-mono font-bold text-foreground">
                      {activeSkill.projects}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Constellation Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <motion.div
            className="glass-morphism rounded-xl p-6 cyber-border text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="w-12 h-12 bg-brand-cyan/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
              <Icon name="Star" size={24} className="text-brand-cyan" />
            </div>
            <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
              {allSkills.length}
            </div>
            <div className="text-sm text-text-secondary">
              Technologies Mastered
            </div>
          </motion.div>

          <motion.div
            className="glass-morphism rounded-xl p-6 cyber-border text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="w-12 h-12 bg-brand-green/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
              <Icon name="TrendingUp" size={24} className="text-brand-green" />
            </div>
            <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
              {Math.round(allSkills.reduce((acc, skill) => acc + skill.level, 0) / allSkills.length)}%
            </div>
            <div className="text-sm text-text-secondary">
              Average Proficiency
            </div>
          </motion.div>

          <motion.div
            className="glass-morphism rounded-xl p-6 cyber-border text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="w-12 h-12 bg-brand-purple/20 rounded-lg flex items-center justify-center mx-auto mb-3 cyber-glow">
              <Icon name="Layers" size={24} className="text-brand-purple" />
            </div>
            <div className="font-orbitron font-bold text-2xl text-foreground mb-1">
              {allSkills.reduce((acc, skill) => acc + skill.projects, 0)}
            </div>
            <div className="text-sm text-text-secondary">
              Total Projects
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SkillConstellation;